/**
 * FleetUnlockModal.jsx
 *
 * Modal displayed when player unlocks a new aircraft tier.
 * Shows congratulations message and list of newly available aircraft.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useWorldState } from '../state/WorldStateContext.jsx';
import { getUnlockMessage, getAircraftForTier } from '../../systems/FleetProgressionSystem.js';
import { AIRCRAFT_DATABASE } from '../../data/aircraft.js';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

export default function FleetUnlockModal() {
  const navigate = useNavigate();
  const { worldState, dismissFleetUnlock } = useWorldState();

  const pendingUnlock = worldState?.pendingFleetUnlock;

  if (!pendingUnlock) return null;

  const unlockInfo = getUnlockMessage(pendingUnlock);
  if (!unlockInfo) return null;

  // Get sample aircraft for this tier
  const tierAircraft = getAircraftForTier(pendingUnlock, AIRCRAFT_DATABASE);
  const displayAircraft = tierAircraft.slice(0, 6); // Show up to 6

  const handleViewMarket = () => {
    dismissFleetUnlock();
    navigate('/aircraft');
  };

  const handleDismiss = () => {
    dismissFleetUnlock();
  };

  // Tier colors
  const tierColors = {
    narrowbody: { primary: '#3b82f6', bg: 'rgba(59, 130, 246, 0.1)' },
    widebody: { primary: '#8b5cf6', bg: 'rgba(139, 92, 246, 0.1)' },
    heavy: { primary: '#f59e0b', bg: 'rgba(245, 158, 11, 0.1)' },
  };
  const colors = tierColors[pendingUnlock] || tierColors.narrowbody;

  return (
    <Dialog open={true} onOpenChange={handleDismiss}>
      <DialogContent
        className="max-w-lg"
        style={{
          backgroundColor: '#0a0a0a',
          borderColor: colors.primary,
          borderWidth: '2px',
        }}
      >
        <DialogHeader>
          <div
            className="text-center py-2 -mx-6 -mt-2 mb-4"
            style={{
              background: `linear-gradient(180deg, ${colors.bg}, transparent)`,
            }}
          >
            <DialogTitle
              className="text-2xl font-bold tracking-wider"
              style={{ color: colors.primary }}
            >
              {unlockInfo.title}
            </DialogTitle>
          </div>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-center" style={{ color: '#a3a3a3' }}>
            {unlockInfo.description}
          </p>

          {/* Aircraft grid */}
          <div className="grid grid-cols-3 gap-2 mt-4">
            {displayAircraft.map((aircraft) => (
              <div
                key={aircraft.model}
                className="p-2 rounded text-center"
                style={{ backgroundColor: '#111', border: '1px solid #222' }}
              >
                <div className="text-sm font-semibold" style={{ color: '#e5e5e5' }}>
                  {aircraft.model}
                </div>
                <div className="text-xs" style={{ color: '#737373' }}>
                  {aircraft.capacity || 0} pax
                </div>
              </div>
            ))}
          </div>

          {tierAircraft.length > 6 && (
            <p className="text-center text-xs" style={{ color: '#525252' }}>
              +{tierAircraft.length - 6} more aircraft available
            </p>
          )}
        </div>

        <DialogFooter className="mt-6 flex gap-3">
          <Button
            variant="outline"
            onClick={handleDismiss}
            style={{
              backgroundColor: 'transparent',
              borderColor: '#333',
              color: '#737373',
            }}
          >
            Later
          </Button>
          <Button
            onClick={handleViewMarket}
            style={{
              backgroundColor: colors.primary,
              color: '#000',
            }}
          >
            VIEW MARKET
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
